import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { TheatrePatient } from './theatre-patient';
import { ITheatrePatientVisit } from '@stockcare-models/ITheatrePatientVisit';
import { Observable, combineLatest, Subject } from 'rxjs';
import { Doctor } from '@stockcare-storage/doctor/doctor';
import { Procedure } from '@stockcare-storage/procedure/procedure';
import { TheatrePatientServicesStorageService } from '@stockcare-storage/theatre-patient-services/theatre-patient-services-storage.service';
import { TheatrePatientMaterialsStorageService } from '@stockcare-storage/theatre-patient-materials/theatre-patient-materials-storage.service';
import { map, take } from 'rxjs/operators';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { PackService } from '@stockcare-services/pack-service/pack.service';

@Injectable({
  providedIn: 'root',
})
export class TheatrePatientStorageService {
  readonly table: string;
  readonly CaseNumberIndex: string;
  enableLocaldb: boolean;
  theatrePatients: TheatrePatient[] = [];
  patientsRefreshed: Subject<boolean> = new Subject<boolean>();

  constructor(
    private internalDatabaseService: InternalDatabaseService,
    private theatrePatientServicesStorageService: TheatrePatientServicesStorageService,
    private theatrePatientMaterialsStorageService: TheatrePatientMaterialsStorageService,
    private readonly settingsService: SettingsService,
    private readonly deviceService: DeviceService
  ) {
    const table = TheatrePatient.getTableDefinition();
    this.table = table.TableName;
    this.CaseNumberIndex = table.Indexes[0].Name;
  }

  hasPatients(): boolean {
    if (this.enableLocaldb) {
      return true;
    }
    return this.theatrePatients.length > 0;
  }
  async addPatients(patients: TheatrePatient[]) {
    await this.internalDatabaseService.addRange(this.table, patients);
  }

  addUpdatePatientDb(patients: ITheatrePatientVisit[]): Observable<boolean> {
    this.getSettings();
    return new Observable((observer) => {
      if (this.enableLocaldb) {
        combineLatest([
          this.deleteAll(),
          this.theatrePatientServicesStorageService.deleteAll(),
          this.theatrePatientMaterialsStorageService.deleteAll(),
        ]).subscribe(() => {
          this.savePatients(patients)
            .pipe(take(1))
            .subscribe(() => {
              observer.next();
            });
        });
      } else {
        combineLatest([
          this.theatrePatientServicesStorageService.deleteAll(),
          this.theatrePatientMaterialsStorageService.deleteAll(),
        ]).subscribe(() => {
          if (!patients || patients.length === 0) {
            this.theatrePatients = [];
            observer.next();
          } else {
            this.theatrePatients = patients.map((p) =>
              this.mapTheatrePatient(p)
            );
            patients.map((s) => {
              combineLatest([
                this.theatrePatientServicesStorageService.addUpdateServices(s),
                this.theatrePatientMaterialsStorageService.addUpdateMaterials(
                  s
                ),
              ]).subscribe(() => {
                observer.next();
              });
            });
          }
        });
      }
    });
  }

  addUpdateSinglePatient(patient: ITheatrePatientVisit): Observable<boolean> {
    this.getSettings();
    return new Observable((observer) => {
      combineLatest([
        this.theatrePatientServicesStorageService.deleteByPatientTheatreVisitId(patient.id),
        this.theatrePatientMaterialsStorageService.deleteByTheatreVisitId(patient.id),
      ]).subscribe(() => {
        combineLatest([
          this.theatrePatientServicesStorageService.addUpdateServices(patient),
          this.theatrePatientMaterialsStorageService.addUpdateMaterials(patient),
        ]).subscribe(() => {
          observer.next();
        });
      });
    });
  }

  private savePatients(patients: ITheatrePatientVisit[]) {
    return new Observable((observer) => {
      if (!patients || patients.length === 0) {
        observer.next();
      }

      const subscriptions = patients.map((x) => this.savePatient(x));

      combineLatest(subscriptions)
        .pipe(take(1))
        .subscribe(() => {
          observer.next();
        });
    });
  }

  private savePatient(x: ITheatrePatientVisit): Observable<boolean> {
    return new Observable((observer) => {
      const mappedPatient = this.mapTheatrePatient(x);
      if (this.enableLocaldb) {
        this.addPatient(mappedPatient).then(() => {
          combineLatest([
            this.theatrePatientServicesStorageService.addUpdateServices(x),
            this.theatrePatientMaterialsStorageService.addUpdateMaterials(x),
          ])
            .pipe(take(1))
            .subscribe(
              (result) => {
                observer.next();
              },
              (error) => {
                observer.error();
              }
            );
        });
      } else {
        this.addPatient(mappedPatient).then(() => {
          observer.next();
        });
      }
    });
  }

  async addPatient(patient: TheatrePatient) {
    if (this.enableLocaldb) {
      return await this.internalDatabaseService.add(this.table, patient);
    } else {
    }
  }

  async getAllPatients(): Promise<TheatrePatient[]> {
    if (this.enableLocaldb) {
      return await this.internalDatabaseService.getAll<TheatrePatient>(
        this.table
      );
    } else {
      return [...this.theatrePatients];
    }
  }
  getPatientById(id: number): Observable<TheatrePatient> {
    return new Observable((observer) => {
      if (this.enableLocaldb) {
        this.internalDatabaseService
          .getByPrimaryKey<TheatrePatient>(this.table, id)
          .then((patient) => {
            observer.next(patient);
          });
      } else {
        const foundPatient = this.theatrePatients.find(
          (patient) => patient.Id === id
        );
        observer.next(foundPatient);
      }
    });
  }

  async deleteAll() {
    await this.internalDatabaseService.deleteAll(this.table);
  }

  async updatePatient(patient: TheatrePatient): Promise<void> {
    if (this.enableLocaldb) {
      await this.internalDatabaseService.update<TheatrePatient>(
        this.table,
        patient
      );
    } else {
      return new Promise<void>((resolve, reject) => {
        this.theatrePatients = this.theatrePatients.filter(
          (x) => x.PatientId !== patient.PatientId
        );
        this.theatrePatients.push(patient);
        resolve();
      });
    }
  }

  addDoctorToPatientVisit(
    theatrePatientId: number,
    doctor: Doctor
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.DoctorIds.push(doctor.Id);
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  updatePatientVisit(
    theatrePatientId: number,
    doctors: Doctor[],
    procedures: Procedure[],
    anaesthetists: Doctor[]
  ): Observable<TheatrePatient> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.DoctorIds = [];
          patient.ProcedureIds = [];
          patient.AnaesthetistIds = [];

          doctors.forEach((doctor) => {
            patient.DoctorIds.push(doctor.Id);
          });

          procedures.forEach((procedure) => {
            patient.ProcedureIds.push(procedure.Id);
          });

          anaesthetists.forEach((anaesthetist) => {
            patient.AnaesthetistIds.push(anaesthetist.Id);
          });

          this.internalDatabaseService
            .update<TheatrePatient>(this.table, patient)
            .then(() => {
              this.getPatientById(theatrePatientId)
                .pipe(take(1))
                .subscribe((theatrePatient) => {
                  observer.next(theatrePatient);
                });
            });
        });
    });
  }

  addProcedureToPatientVisit(
    theatrePatientId: number,
    procedure: Procedure
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.ProcedureIds.push(procedure.Id);
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  addTextProcedureToPatientVisit(
    theatrePatientId: number,
    procedure: string
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.FreeTextProcedure = procedure;
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  addAnaesthetistToPatientVisit(
    theatrePatientId: number,
    doctor: Doctor
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.AnaesthetistIds.push(doctor.Id);
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  removeAnaesthetistFromPatientVisit(
    theatrePatientId: number,
    anaesthetist: Doctor
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.AnaesthetistIds = patient.AnaesthetistIds.filter(
            (x) => x !== anaesthetist.Id
          );
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  removeDoctorFromPatientVisit(
    theatrePatientId: number,
    doctor: Doctor
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.DoctorIds = patient.DoctorIds.filter((x) => x !== doctor.Id);
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  removeProcedureFromPatientVisit(
    theatrePatientId: number,
    procedure: Procedure
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.ProcedureIds = patient.ProcedureIds.filter(
            (x) => x !== procedure.Id
          );
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  removeAppProceduresFromPatientVisit(
    theatrePatientId: number
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getPatientById(theatrePatientId)
        .pipe(take(1))
        .subscribe((patient) => {
          patient.ProcedureIds = [];
          if (this.enableLocaldb) {
            this.internalDatabaseService
              .update<TheatrePatient>(this.table, patient)
              .then(() => {
                observer.next(true);
              });
          } else {
            observer.next(true);
          }
        });
    });
  }

  map(patients: ITheatrePatientVisit[]) {
    const dbPatients: TheatrePatient[] = [];

    for (const patient of patients) {
      const dbPatient = this.mapTheatrePatient(patient);
      dbPatients.push(dbPatient);
    }

    return dbPatients;
  }

  mapTheatrePatient(patient: ITheatrePatientVisit) {
    const dbPatient = new TheatrePatient();
    dbPatient.Id = patient.id;
    dbPatient.PatientId = patient.patientId;
    dbPatient.CaseNumber = patient.caseNumber;
    dbPatient.StartDate = patient.startDate;
    dbPatient.EndDate = patient.endDate;
    dbPatient.OperatingStartDate = patient.operatingStartDate;
    dbPatient.OperatingEndDate = patient.operatingEndDate;
    dbPatient.TimeInOperatingRoomSeconds = this.buildTimeInOperatingRoom(
      patient.operatingStartDate,
      patient.operatingEndDate,
      patient.timeInOperatingRoomSeconds
    );
    dbPatient.TimeInRecoveryRoomSeconds = patient.timeInRecoveryRoomSeconds;
    dbPatient.PatientStatus = patient.patientStatus;
    dbPatient.PhysicalLocationId = patient.physicalLocationId;
    dbPatient.Name = patient.name;
    dbPatient.DoctorIds = patient.doctors.map((x) => x.id);
    dbPatient.AnaesthetistIds = patient.anaesthetists.map((x) => x.id);
    dbPatient.ProcedureIds = patient.procedures.map((x) => x.id);
    dbPatient.OperatingRoomNumber = patient.operatingRoomNumber;
    dbPatient.OperatingRoomName = patient.operatingRoomName;
    dbPatient.FreeTextProcedure = patient.freeTextProcedure;
    dbPatient.MedicalAidDescription = patient.medicalAidDescription;
    dbPatient.ProcedureTypeId = patient.procedureTypeId;
    dbPatient.ProcedureType = patient.procedureType;
    return dbPatient;
  }

  private buildTimeInOperatingRoom(
    operatingStartDate: Date,
    operatingEndDate: Date,
    timeInOperatingRoomSeconds: number
  ): number {
    if (this.isMinimumDateTimeValue(operatingStartDate)) {
      return 0;
    }

    if (
      !this.isMinimumDateTimeValue(operatingStartDate) &&
      !this.isMinimumDateTimeValue(operatingEndDate)
    ) {
      return timeInOperatingRoomSeconds;
    }

    var endDate = new Date();
    var startDate = new Date(operatingStartDate);
    var seconds = (endDate.getTime() - startDate.getTime()) / 1000;
    return seconds;
  }

  isMinimumDateTimeValue(operatingStartDate: Date): Boolean {
    return (
      new Date(operatingStartDate).getTime() ===
      new Date("0001-01-01T02:00:00").getTime()
    );
  }

  mapFromTheatrePatient(
    dbPatient: TheatrePatient,
    physicalLocationTheatreRoomId: number
  ): Observable<ITheatrePatientVisit> {
    return new Observable((observer) => {
      const getAllServicesSubscription =
        this.theatrePatientServicesStorageService.getAllByTheatrePatientId(
          dbPatient.Id
        );

      const getAllMaterialsSubscription =
        this.theatrePatientMaterialsStorageService.getAllByTheatrePatientId(
          dbPatient.Id
        );

      combineLatest([getAllServicesSubscription, getAllMaterialsSubscription])
        .pipe(map(([services, materials]) => ({ services, materials })))
        .subscribe((result) => {
          const patient: ITheatrePatientVisit = {
            id: dbPatient.Id,
            patientId: dbPatient.PatientId,
            caseNumber: dbPatient.CaseNumber,
            startDate: dbPatient.StartDate,
            endDate: dbPatient.EndDate,
            deviceId: this.deviceService.getDeviceId(),
            institutionId: this.deviceService.getInstitutionId(),
            physicalLocationTheatreRoomId: physicalLocationTheatreRoomId,
            operatingStartDate: dbPatient.OperatingStartDate,
            operatingEndDate: dbPatient.OperatingEndDate,
            timeInOperatingRoomSeconds: dbPatient.TimeInOperatingRoomSeconds,
            timeInRecoveryRoomSeconds: dbPatient.TimeInRecoveryRoomSeconds,
            patientStatus: dbPatient.PatientStatus,
            physicalLocationId: dbPatient.PhysicalLocationId,
            name: dbPatient.Name,
            doctorIds: dbPatient.DoctorIds,
            anaesthetistIds: dbPatient.AnaesthetistIds,
            procedureIds: dbPatient.ProcedureIds,
            freeTextProcedure: dbPatient.FreeTextProcedure,
            operatingRoomNumber: dbPatient.OperatingRoomNumber,
            operatingRoomName: dbPatient.OperatingRoomName,
            medicalAidDescription: dbPatient.MedicalAidDescription,
            doctors: [],
            anaesthetists: [],
            procedures: [],
            materials: result.materials
              ? result.materials.map((x) =>
                  this.theatrePatientMaterialsStorageService.mapFromTheatrePatientMaterial(
                    x
                  )
                )
              : [],
            services: result.services
              ? result.services.map((x) =>
                  this.theatrePatientServicesStorageService.mapFromTheatrePatientService(
                    x
                  )
                )
              : [],
            procedureTypeId: dbPatient.ProcedureTypeId,
            procedureType: dbPatient.ProcedureType,
          };

          observer.next(patient);
        });
    });
  }

  getSettings() {
    this.enableLocaldb = this.settingsService.getBooleanSetting(
      Settings.ENABLE_INDEXED_DB
    );
  }
}
